// Top-level layout — vaul drawer for mobile (Sprint mobile-drawer-vaul,
// 2026-05-12). Replaces the previous Radix-Sheet + edge-swipe-detector
// pair with a finger-following drawer that drags from anywhere on the
// viewport and covers 100 % of the screen, matching ChatGPT iOS.
//
// Desktop: [Sidebar | ChatView] inside a flex row. Unchanged.
// Mobile : [ChatView] full-width with Sidebar in a vaul Drawer that
//          follows the finger 1:1. Drag from anywhere; snap on release.

import { useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { ChatView } from './ChatView';
import { WorkspaceColumn } from './WorkspaceColumn';
import { Toaster } from './ui/sonner';
import { NetworkBanner } from './organisms/NetworkBanner';
import { RunnerStatusBanner } from './topbar/RunnerStatusBanner';
import { SpawnPreparingToast } from './agents/SpawnPreparingToast';
import { MD_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { useResizableSidebar } from '../hooks/useResizableSidebar';
import { useSwipeOpenDrawer } from '../hooks/useSwipeOpenDrawer';
import { useTranslation } from '../i18n';
import { Drawer, DrawerContent } from './ui/drawer';
import { getSocket } from '../lib/socket';
import { subscribeProjectsToSocket } from '../stores/projects.store';

export interface DashboardLayoutProps {
  onLogout: () => void;
}

export function DashboardLayout({ onLogout }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MD_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { width: sidebarWidth, isResizing, startResizing } = useResizableSidebar();

  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Sprint mobile-drawer-swipe-open-fix (2026-05-13) — vaul does NOT mount
  // the drawer node until first open, so swipe-to-open needs an external
  // listener. Mobile-only; desktop keeps the resizable sidebar layout.
  useSwipeOpenDrawer({
    open: drawerOpen,
    onOpen: () => setDrawerOpen(true),
    enabled: !isDesktop,
  });

  // Sprint 11b.2 — subscribe the projects store to backend socket events
  // so multi-tab + multi-device sync (D-007 §2.5) is automatic. Wired at
  // layout level so the subscription survives sidebar / chat re-renders.
  useEffect(() => {
    const socket = getSocket();
    return subscribeProjectsToSocket(socket);
  }, []);

  return (
    <div
      className="flex flex-col h-full w-full"
      data-testid="app-shell"
      style={isResizing ? { cursor: 'col-resize', userSelect: 'none' } : undefined}
    >
      <NetworkBanner />
      <RunnerStatusBanner />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Workspace column — desktop only, 60 px */}
        <div className="hidden md:flex shrink-0 h-full">
          <WorkspaceColumn />
        </div>

        <div
          className="hidden md:flex shrink-0 h-full relative"
          style={{ width: sidebarWidth }}
          data-testid="sidebar-column"
        >
          <Sidebar onLogout={onLogout} />
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t('sidebar.resizeAriaLabel')}
            onPointerDown={startResizing}
            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-primary/20"
            style={{ touchAction: 'none' }}
            data-testid="sidebar-resize-handle"
          />
        </div>

        <div className="flex-1 min-w-0 h-full">
          <ChatView onOpenSidebar={() => setDrawerOpen(true)} />
        </div>
      </div>

      {/* Mobile drawer — vaul, finger-following, full viewport width.
          Drag from anywhere (vaul default); release behaviour decided by
          distance + velocity. `direction="left"` so the drag axis is x. */}
      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        direction="left"
      >
        <DrawerContent
          className="p-0 [&>button]:hidden"
          data-testid="mobile-drawer"
        >
          <div className="flex h-full">
            <WorkspaceColumn />
            <div className="flex-1 min-w-0 h-full">
              <Sidebar
                onAgentSelected={() => setDrawerOpen(false)}
                onLogout={onLogout}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Toaster position="bottom-center" />
      <SpawnPreparingToast />
    </div>
  );
}
